import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Tag
} from 'antd'
import { ModalProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useState } from 'react'

import {
  useClassifierServiceGetCommonClassifierElementListEndpointKey,
  usePatientTreatmentServiceCreatePatientTreatmentPlanAdverseEffectEntryEndpoint,
  usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierService,
  ClassifierType,
  GetCommonClassifierElementResponse
} from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { ModalFooterProps, SaveConfirm } from 'src/components'
import { useClassifierId } from 'src/hooks'
import {
  dayMonthYearBackSlash,
  disabledFutureDate,
  yearMonthDaySlash
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from './styles'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const fieldsToValidate = ['date', 'events', 'comment']

const Footer = ({ onCancel, onSubmit }: ModalFooterProps) => {
  const { t } = useLocales()

  return (
    <Flex align="center" justify="center" gap={8}>
      <Button onClick={onCancel}>{t('common.cancel')}</Button>

      <SaveConfirm title={`${t('common.confirm')}?`} onConfirm={onSubmit}>
        <Button type="primary">{t('common.confirm')}</Button>
      </SaveConfirm>
    </Flex>
  )
}

type SelectedTags = {
  value?: string
  label?: string
}

export const ReactionsModal = ({ id, onCancel }: Props) => {
  const [selectedItems, setSelectedItems] = useState<SelectedTags[]>([])
  const { currentLang } = useLocales()

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { t } = useLocales()

  const { mutate: addAdverseEffects } =
    usePatientTreatmentServiceCreatePatientTreatmentPlanAdverseEffectEntryEndpoint(
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [
              usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryListEndpointKey,
              String(id)
            ]
          })
          onCancel()
        },
        onError: (err: ApiError) => formErrorHandler(err.body.errors)
      }
    )

  const { styles } = useStyles()

  const onSubmit = async () => {
    const values = await form?.validateFields(fieldsToValidate)

    const newEntry = {
      adverseEffectIds: values?.events?.map(
        (item: { value: string }) => item?.value
      ),
      comments: values.comment,
      recordedDate: dayjs(values.date).format(yearMonthDaySlash)
    }

    addAdverseEffects({
      id: String(id),
      requestBody: {
        adverseEffectEntry: newEntry
      }
    })
  }

  const onChange = (value: SelectedTags[]) => {
    setSelectedItems(value)
    form.setFieldValue('events', value)
  }

  const removeTag = (item: SelectedTags) => {
    const newValue = selectedItems.filter(
      (selectedItem) => selectedItem?.value !== item?.value
    )
    onChange(newValue)
  }

  const classifierId = useClassifierId(
    ClassifierType.ACCUSE,
    ClassifierCategory.TREATMENT
  )

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetCommonClassifierElementListEndpointKey,
      String(classifierId)
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getCommonClassifierElementListEndpoint(
          String(classifierId),
          pageParam,
          DEFAULT_PER_PAGE
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetCommonClassifierElementResponse[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!classifierId
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data?.map((item: GetCommonClassifierElementResponse) => ({
        value: item?.id,
        label: item?.translation?.[currentLang?.value]
      })) || []
  )

  const dropdownRender = () => (
    <Flex vertical gap={4} className={styles.dropdownRender}>
      {options?.map((option) => (
        <Checkbox
          key={option.value}
          checked={selectedItems.some((item) => item.value === option?.value)}
          onChange={(e) => {
            const newValue = e.target.checked
              ? [...selectedItems, option]
              : selectedItems.filter((item) => item.value !== option.value)
            onChange(newValue)
          }}
        >
          {option.label}
        </Checkbox>
      ))}
    </Flex>
  )

  return (
    <Modal
      open
      maskClosable={false}
      footer={Footer({
        onCancel,
        onSubmit: () => {
          void onSubmit()
        }
      })}
      title={t('treatments.report_reactions')}
      onCancel={onCancel}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Flex vertical gap={16}>
          <Form.Item
            label={t('consultations.adverse_reaction_date')}
            name="date"
            rules={requiredRules}
          >
            <DatePicker
              format={dayMonthYearBackSlash}
              className="full-width"
              placeholder={t('consultations.adverse_reaction_date')}
              disabledDate={disabledFutureDate}
            />
          </Form.Item>

          <Form.Item
            label={t('consultations.accuse')}
            name="events"
            rules={requiredRules}
          >
            <Select
              allowClear
              filterOption={false}
              options={options}
              loading={isFetching}
              showSearch={false}
              placeholder={t('common.select_from_list')}
              dropdownRender={dropdownRender}
              onPopupScroll={(e) => {
                const target = e.target as HTMLElement
                if (
                  target.scrollTop + target.offsetHeight ===
                  target.scrollHeight
                )
                  fetchNextPage()
              }}
              notFoundContent={
                isFetching ? (
                  <Flex justify="center">
                    <Spin spinning />
                  </Flex>
                ) : (
                  <Empty />
                )
              }
            />
            <Flex gap={8} className={styles.tagsWrapper}>
              {selectedItems?.map((item) => (
                <Tag
                  className={styles.tags}
                  key={item?.value}
                  closable
                  onClose={() => removeTag(item)}
                >
                  {item?.label}
                </Tag>
              ))}
            </Flex>
          </Form.Item>

          <Form.Item
            label={t('treatments.report_reactions_desc')}
            name="comment"
            rules={requiredRules}
          >
            <Input.TextArea
              rows={3}
              placeholder={t('treatments.report_reactions_placeholder')}
            />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  )
}
